import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Package, KeyRound, ExternalLink, Ban } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/apiClient";
import { PageHeader, Loader, EmptyState, StatusBadge } from "@/components/common";
import { ImageUpload } from "@/pages/admin/AdminUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const emptyCourse = { title: "", description: "", type: "additional", format: "text", price: "", payment_details: "", cover_url: null, published: false };

function Courses() {
  const [courses, setCourses] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCourse);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const load = async () => setCourses((await api.get("/admin/courses")).data.filter((c) => c.type === "additional"));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.title) return toast.error("Title required");
    if (form.id) await api.put(`/admin/courses/${form.id}`, form); else await api.post("/admin/courses", { ...form, type: "additional" });
    toast.success("Saved. Manage its lessons under the Courses tab."); setOpen(false); setForm(emptyCourse); load();
  };
  const del = async (id) => { await api.delete(`/admin/courses/${id}`); load(); };
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={() => { setForm(emptyCourse); setOpen(true); }} data-testid="add-product-course"><Plus className="w-4 h-4 mr-1.5" /> Add Niche Course</Button></div>
      {!courses ? <Loader /> : courses.length === 0 ? <EmptyState icon={Package} title="No additional courses" description="Create paid niche courses. Add lessons via the Courses tab." testId="products-courses-empty" /> : (
        <div className="space-y-3">
          {courses.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3" data-testid={`pcourse-${c.id}`}>
              <div><p className="font-medium">{c.title}</p><p className="text-xs text-muted-foreground capitalize">{c.format} · {c.price || "—"}</p></div>
              <div className="flex items-center gap-2">
                <StatusBadge status={c.published ? "approved" : "rejected"} />
                <Button size="sm" variant="ghost" onClick={() => { setForm(c); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "Add"} Niche Course</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="pc-title" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="pc-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Format</Label>
                <Select value={form.format} onValueChange={(v) => set("format", v)}>
                  <SelectTrigger data-testid="pc-format"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="text">Text / Self-paced</SelectItem><SelectItem value="video">Video-based</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Price</Label><Input value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="₱1,500" data-testid="pc-price" /></div>
            </div>
            <div><Label>Payment Details (shown to students)</Label><Textarea value={form.payment_details} onChange={(e) => set("payment_details", e.target.value)} placeholder="Bank / Maya details..." data-testid="pc-payment" /></div>
            <ImageUpload label="Cover image" value={form.cover_url} onChange={(v) => set("cover_url", v)} testId="pc-cover" />
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="pc-published" /> Published</label>
          </div>
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-pcourse">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Proofs() {
  const [proofs, setProofs] = useState(null);
  const [status, setStatus] = useState("pending");
  const [codeFor, setCodeFor] = useState(null);
  const load = async () => setProofs((await api.get(`/admin/payment-proofs?status=${status}`)).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const act = async (id, action) => { await api.post(`/admin/payment-proofs/${id}`, { action }); toast.success(action); load(); };
  const genCode = async () => {
    const { data } = await api.post("/admin/access-codes", { product_id: codeFor.product_id, student_name: codeFor.student_name, student_email: codeFor.student_email });
    toast.success(`Code generated: ${data.code}`); setCodeFor({ ...codeFor, generated: data.code });
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="proof-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="rejected">Rejected</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
      </div>
      {!proofs ? <Loader /> : proofs.length === 0 ? <EmptyState icon={KeyRound} title="No payment proofs" testId="proofs-empty" /> : (
        <div className="space-y-3">
          {proofs.map((p) => (
            <div key={p.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm" data-testid={`proof-${p.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div><p className="font-medium">{p.student_name}</p><p className="text-xs text-muted-foreground">{p.student_email} · product: {p.product_id}</p></div>
                <StatusBadge status={p.status} />
              </div>
              {p.file_url && <a href={fileUrl(p.file_url, true)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-teal text-sm mt-2 hover:underline">View receipt <ExternalLink className="w-3.5 h-3.5" /></a>}
              <div className="flex gap-2 mt-3">
                {p.status === "pending" && <>
                  <Button size="sm" className="rounded-full bg-brandgreen hover:bg-brandgreen/90" onClick={() => act(p.id, "approved")} data-testid={`approve-proof-${p.id}`}>Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(p.id, "rejected")}>Reject</Button>
                </>}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setCodeFor({ product_id: p.product_id, student_name: p.student_name, student_email: p.student_email })} data-testid={`gencode-${p.id}`}><KeyRound className="w-4 h-4 mr-1.5" /> Generate Code</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!codeFor} onOpenChange={(o) => !o && setCodeFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Access Code</DialogTitle></DialogHeader>
          {codeFor && (
            <div className="space-y-3">
              <div><Label>Product ID</Label><Input value={codeFor.product_id} onChange={(e) => setCodeFor({ ...codeFor, product_id: e.target.value })} data-testid="code-product" /></div>
              <div><Label>Student Name</Label><Input value={codeFor.student_name} onChange={(e) => setCodeFor({ ...codeFor, student_name: e.target.value })} data-testid="code-name" /></div>
              <div><Label>Student Email</Label><Input value={codeFor.student_email} onChange={(e) => setCodeFor({ ...codeFor, student_email: e.target.value })} data-testid="code-email" /></div>
              {codeFor.generated && <div className="p-3 rounded-xl bg-brandgreen/10 text-center"><p className="text-sm">Code:</p><p className="font-heading text-2xl font-bold text-brandgreen tracking-widest">{codeFor.generated}</p></div>}
            </div>
          )}
          <DialogFooter><Button className="rounded-full bg-teal hover:bg-teal/90" onClick={genCode} data-testid="do-gencode">Generate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Codes() {
  const [codes, setCodes] = useState(null);
  const load = async () => setCodes((await api.get("/admin/access-codes")).data);
  useEffect(() => { load(); }, []);
  const revoke = async (id) => { await api.post(`/admin/access-codes/${id}/revoke`); toast.success("Revoked"); load(); };
  return !codes ? <Loader /> : codes.length === 0 ? <EmptyState icon={KeyRound} title="No codes issued yet" testId="codes-empty" /> : (
    <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left"><tr><th className="p-3">Code</th><th className="p-3">Student</th><th className="p-3 hidden sm:table-cell">Product</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
        <tbody>
          {codes.map((c) => (
            <tr key={c.id} className="border-t border-border" data-testid={`code-${c.id}`}>
              <td className="p-3 font-mono font-medium">{c.code}</td>
              <td className="p-3"><p>{c.student_name}</p><p className="text-xs text-muted-foreground">{c.student_email}</p></td>
              <td className="p-3 hidden sm:table-cell text-muted-foreground">{c.product_id}</td>
              <td className="p-3"><StatusBadge status={c.revoked ? "rejected" : c.redeemed ? "approved" : "pending"} /></td>
              <td className="p-3">{!c.revoked && <Button size="sm" variant="ghost" onClick={() => revoke(c.id)} data-testid={`revoke-${c.id}`}><Ban className="w-4 h-4 text-red-500" /></Button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SLOT_STATUS = {
  available: "bg-teal/10 text-teal", requested: "bg-amber-100 text-amber-700",
  booked: "bg-green-100 text-green-700", unavailable: "bg-slate-100 text-slate-500",
};
function MentorshipReqs() {
  const [slots, setSlots] = useState(null);
  const [date, setDate] = useState("");
  const [times, setTimes] = useState("");
  const load = async () => setSlots((await api.get("/admin/mentorship/slots")).data);
  useEffect(() => { load(); }, []);
  const addSlots = async () => {
    if (!date || !times.trim()) return toast.error("Enter a date and at least one time");
    const list = times.split(",").map((t) => t.trim()).filter(Boolean);
    try { await api.post("/admin/mentorship/slots", { date, times: list }); toast.success("Slots added"); setTimes(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const act = async (id, action) => { await api.post(`/admin/mentorship/slots/${id}/action`, { action }); toast.success("Updated"); load(); };
  const del = async (id) => { await api.delete(`/admin/mentorship/slots/${id}`); load(); };

  const byDate = {};
  (slots || []).forEach((s) => { (byDate[s.date] = byDate[s.date] || []).push(s); });
  const dates = Object.keys(byDate).sort();

  return (
    <div>
      <div className="bg-white border border-border rounded-2xl p-5 shadow-sm mb-5" data-testid="add-slots">
        <h3 className="font-heading font-semibold mb-3">Open availability</h3>
        <div className="grid sm:grid-cols-[auto_1fr_auto] gap-3 items-end">
          <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="slot-date" /></div>
          <div><Label>Times (24h, comma-separated)</Label><Input value={times} onChange={(e) => setTimes(e.target.value)} placeholder="09:00, 14:30, 16:00" data-testid="slot-times" /></div>
          <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={addSlots} data-testid="add-slots-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Slots</Button>
        </div>
      </div>
      {!slots ? <Loader /> : dates.length === 0 ? <EmptyState icon={Package} title="No slots yet" description="Add availability above so students can request sessions." testId="slots-empty" /> : (
        <div className="space-y-5">
          {dates.map((d) => (
            <div key={d} data-testid={`adm-cal-${d}`}>
              <p className="font-semibold text-sm mb-2">{new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p>
              <div className="space-y-2">
                {byDate[d].map((s) => (
                  <div key={s.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3 flex-wrap" data-testid={`adm-slot-${s.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{s.time}</span>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${SLOT_STATUS[s.status] || "bg-slate-100"}`} data-testid={`slot-status-${s.id}`}>{s.status}</span>
                      {s.student_name && <span className="text-xs text-muted-foreground">{s.student_name} · {s.student_email}{s.niche ? ` · ${s.niche}` : ""}</span>}
                    </div>
                    <div className="flex gap-1.5">
                      {s.status === "requested" && <>
                        <Button size="sm" className="rounded-full bg-brandgreen hover:bg-brandgreen/90" onClick={() => act(s.id, "confirm")} data-testid={`confirm-slot-${s.id}`}>Confirm</Button>
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(s.id, "decline")} data-testid={`decline-slot-${s.id}`}>Decline</Button>
                      </>}
                      {s.status === "available" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(s.id, "block")}>Block</Button>}
                      {s.status === "unavailable" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => act(s.id, "open")}>Open</Button>}
                      {s.note && <span className="text-xs text-muted-foreground max-w-[200px] truncate" title={s.note}>“{s.note}”</span>}
                      <Button size="sm" variant="ghost" onClick={() => del(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const BOOKING_STATUS = { pending: "bg-amber-100 text-amber-700", paid: "bg-sky-100 text-sky-700", confirmed: "bg-green-100 text-green-700", cancelled: "bg-slate-100 text-slate-500" };

function MentorshipConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/mentorship/config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return <Loader />;
  const setK = (grp, k, v) => setCfg((c) => ({ ...c, [grp]: { ...c[grp], [k]: v } }));
  const save = async () => { await api.put("/admin/mentorship/config", cfg); toast.success("Session details saved"); };
  const Editor = ({ grp, label }) => (
    <div className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-3" data-testid={`cfg-${grp}`}>
      <h4 className="font-heading font-semibold">{label}</h4>
      <div><Label>Description</Label><Textarea value={cfg[grp].description || ""} onChange={(e) => setK(grp, "description", e.target.value)} data-testid={`cfg-${grp}-desc`} /></div>
      <div><Label>Policies</Label><Textarea value={cfg[grp].policies || ""} onChange={(e) => setK(grp, "policies", e.target.value)} data-testid={`cfg-${grp}-policies`} /></div>
      <div><Label>Price</Label><Input value={cfg[grp].price || ""} onChange={(e) => setK(grp, "price", e.target.value)} data-testid={`cfg-${grp}-price`} /></div>
      <ImageUpload label="Payment QR code" value={cfg[grp].qr_url} onChange={(v) => setK(grp, "qr_url", v)} testId={`cfg-${grp}-qr`} />
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4"><Editor grp="group" label="Group Mentorship" /><Editor grp="one" label="One-on-One Mentorship" /></div>
      <Button className="rounded-full bg-teal hover:bg-teal/90" onClick={save} data-testid="save-mentorship-config">Save Session Details</Button>
    </div>
  );
}

function MentorshipBookings() {
  const [rows, setRows] = useState(null);
  const load = async () => setRows((await api.get("/admin/mentorship/bookings")).data);
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/mentorship/bookings/${id}`, { status }); toast.success("Status updated"); load(); };
  if (!rows) return <Loader />;
  if (rows.length === 0) return <EmptyState icon={Package} title="No bookings yet" description="Incoming session bookings will appear here." testId="bookings-empty" />;
  return (
    <div className="space-y-3" data-testid="mentorship-bookings">
      {rows.map((b) => (
        <div key={b.id} className="bg-white border border-border rounded-2xl p-4 shadow-sm flex items-center justify-between gap-3 flex-wrap" data-testid={`booking-${b.id}`}>
          <div className="min-w-0">
            <p className="font-medium">{b.name} <span className="text-xs text-muted-foreground">· {b.session_type === "group" ? "Group" : "1-on-1"}</span></p>
            <p className="text-xs text-muted-foreground">{b.email} · {b.preferred_time}{b.note ? ` · “${b.note}”` : ""}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${BOOKING_STATUS[b.status] || "bg-slate-100"}`}>{b.status}</span>
            <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
              <SelectTrigger className="w-32 h-8" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
}

function MentorshipAdmin() {
  return (
    <div className="space-y-8">
      <div><h3 className="font-heading font-semibold mb-3">Session Details & Pricing</h3><MentorshipConfig /></div>
      <div><h3 className="font-heading font-semibold mb-3">Group Availability</h3><MentorshipReqs /></div>
      <div><h3 className="font-heading font-semibold mb-3">All Bookings</h3><MentorshipBookings /></div>
    </div>
  );
}

export default function AdminProducts() {
  return (
    <div>
      <PageHeader title="Products & Access" subtitle="Niche courses, payment verification, and access codes" />
      <Tabs defaultValue="courses">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="courses" data-testid="ptab-courses">Niche Courses</TabsTrigger>
          <TabsTrigger value="proofs" data-testid="ptab-proofs">Payment Proofs</TabsTrigger>
          <TabsTrigger value="codes" data-testid="ptab-codes">Access Codes</TabsTrigger>
          <TabsTrigger value="mentorship" data-testid="ptab-mentorship">Mentorship</TabsTrigger>
        </TabsList>
        <TabsContent value="courses"><Courses /></TabsContent>
        <TabsContent value="proofs"><Proofs /></TabsContent>
        <TabsContent value="codes"><Codes /></TabsContent>
        <TabsContent value="mentorship"><MentorshipAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
